import type {
    ClubSocialOverviewInterface,
    FacebookConnectionInterface,
    InstagramConnectionInterface,
} from 'services/social.service';

/**
 * The one place the screens ask "can this club use this channel".
 *
 * A club has two independent connections — its Facebook page and its
 * Instagram account (Instagram Login) — and may have either, both or neither.
 * Mirrors the backend's `ClubChannels`:
 *   • a channel EXISTS while its connection is not `Disconnected` — a post may
 *     be planned on it (the club can still log in again before it is due);
 *   • a channel is LIVE while its connection is `Connected` — only then can a
 *     post go out on it right away.
 */

type AnyConnection =
    | FacebookConnectionInterface
    | InstagramConnectionInterface
    | null
    | undefined;

const exists = (connection: AnyConnection): boolean =>
    !!connection && connection.status !== 'Disconnected';

const live = (connection: AnyConnection): boolean =>
    !!connection && connection.status === 'Connected';

/** The club has a Facebook page connected (possibly waiting for a new login). */
export const hasFacebook = (
    club?: ClubSocialOverviewInterface | null
): boolean => exists(club?.connection);

/** The club has an Instagram account connected (possibly waiting for a new login). */
export const hasInstagram = (
    club?: ClubSocialOverviewInterface | null
): boolean => exists(club?.instagram_connection);

/** Posts can go out to the club's Facebook page right now. */
export const facebookLive = (
    club?: ClubSocialOverviewInterface | null
): boolean => live(club?.connection);

/** Posts can go out to the club's Instagram account right now. */
export const instagramLive = (
    club?: ClubSocialOverviewInterface | null
): boolean => live(club?.instagram_connection);

/** The club has at least one channel — the clubs the composer, the list and the automation offer. */
export const hasAnyChannel = (
    club?: ClubSocialOverviewInterface | null
): boolean => hasFacebook(club) || hasInstagram(club);

/** The club has at least one channel that is live right now. */
export const hasAnyLiveChannel = (
    club?: ClubSocialOverviewInterface | null
): boolean => facebookLive(club) || instagramLive(club);

/** Username of the club's Instagram account (without `@`) while it is connected, else null. */
export const instagramUsernameOf = (
    club?: ClubSocialOverviewInterface | null
): string | null =>
    hasInstagram(club)
        ? club?.instagram_connection?.instagram_username ?? null
        : null;

/** Name of the club's Facebook page while it is connected, else null. */
export const facebookPageNameOf = (
    club?: ClubSocialOverviewInterface | null
): string | null =>
    hasFacebook(club) ? club?.connection?.page_name ?? null : null;

/**
 * "Club — page · @instagram" for a club picker: the club name followed by
 * whatever it publishes to (only the page, only the account, or both).
 */
export const clubChannelsLabel = (
    club: ClubSocialOverviewInterface
): string => {
    const targets = [
        facebookPageNameOf(club),
        instagramUsernameOf(club) ? `@${instagramUsernameOf(club)}` : null,
    ].filter(Boolean);

    return targets.length > 0
        ? `${club.club_name} — ${targets.join(' · ')}`
        : club.club_name;
};

/** Public profile of an Instagram account. */
export const instagramProfileUrl = (username: string): string =>
    `https://www.instagram.com/${encodeURIComponent(username)}/`;
